import type { SVGProps } from "react";

import { cx } from "@/lib/cx";

/**
 * Stats glyphs — a flame for the task streak, a timer for focus, a dumbbell for
 * workouts, and a chevron-ish spark for XP.
 *
 * Drawn inline for the same reason as the navigation icons: four shapes do not
 * justify an icon package, and `currentColor` lets each one take its colour
 * from the compact cell or status surface it sits in.
 *
 * ## They are sized in `em`, never in pixels
 *
 * These glyphs land in differently sized compact cells and status surfaces.
 * The old file hard-coded `size-[15px]`, so callers that supplied their own
 * size got two `width` declarations resolved by stylesheet order rather than
 * by intent.
 *
 * `size-[1em]` removes the question: **the container sets the size by setting a
 * font-size**, exactly as `ui/StatusPanel` already assumes through the type ramp.
 * `className` here is for colour.
 */

type IconProps = SVGProps<SVGSVGElement>;

function Icon({ children, className, ...rest }: IconProps) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={cx("size-[1em] shrink-0", className)}
      {...rest}
    >
      {children}
    </svg>
  );
}

/** Task streak. */
export function StatsFlameIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M10 2.5c2.6 2.4 4.6 4.6 4.6 7.4a4.6 4.6 0 0 1-9.2 0c0-1.4.5-2.5 1.3-3.6.4 1 1 1.6 1.8 1.9.3-2.3-.1-4 1.5-5.7Z" />
    </Icon>
  );
}

/** Focus / pomodoro. */
export function TimerIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="10" cy="11.5" r="6.2" />
      <path d="M10 8.4v3.1l2 1.4" />
      <path d="M8 2.4h4" />
    </Icon>
  );
}

/** Workout. */
export function StatsDumbbellIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4.2 7.2v5.6M6.9 5.6v8.8M13.1 5.6v8.8M15.8 7.2v5.6" />
      <path d="M6.9 10h6.2" />
    </Icon>
  );
}

/** XP and levels — the spark on the hero's ring. */
export function SparkIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M10 2.6l1.9 4.4 4.5 1.7-4.5 1.7L10 15l-1.9-4.6-4.5-1.7 4.5-1.7L10 2.6Z" />
    </Icon>
  );
}
